import { FC, useEffect, useRef, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Stack,
  Grid,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import DropDown from "@/components/forms/inputs/dropDown";
import GenericDateTime from "@/components/dateTime/genericDateTime";
import dayjs from "dayjs";
import styles from "@/components/todos/todoCreateEditModal.module.css";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useTranslation } from "react-i18next";
import { IDropDownOption } from "@/types/DropDown";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import { KAER_COMPANY_ID } from "@/helper/systemIds";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

type TodoModalModes = "template" | "manual" | "kaer_internal";

const GBU_TEMPLATE_NAME = "offener_punkt_aus_der_gefaehrdungsbeurteilung";

const dueDateInDaysAtNoon = (days: number) =>
  dayjs().add(days, "day").hour(12).minute(0).second(0);

const defaultCreateDueDate = (
  prefilledDueDate: TodoCreateEditModalProps["prefilledDueDate"],
  resolvedInitialCreateMode: TodoModalModes
) =>
  prefilledDueDate ??
  (resolvedInitialCreateMode === "kaer_internal"
    ? dueDateInDaysAtNoon(7)
    : dueDateInDaysAtNoon(14));

interface TodoCreateEditModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode?: "create" | "edit";
  todoToEdit?: any;
  preExistingSubject?: { value: string; label: string };
  prefilledName?: string;
  prefilledDueDate?: any;
  hideNonMandatoryFields?: boolean;
  usePersistedSubjectSearchValue?: boolean;
  isBatchMode?: boolean;
  selectedCompanies?: string[];
  initialCreateMode?: TodoModalModes;
  enableKaerInternalTab?: boolean;
  prefilledResponsibleSubject?: { value: string; label: string };
  forcedTextTarget?: { link: string; linkTitle: string };
  showTextTargetWhenHidden?: boolean;
  selectedTableRows?: any[];
}

const TodoCreateEditModal: FC<TodoCreateEditModalProps> = ({
  open,
  onClose,
  onSuccess,
  mode = "create",
  todoToEdit,
  preExistingSubject,
  prefilledName = "",
  prefilledDueDate,
  hideNonMandatoryFields = false,
  usePersistedSubjectSearchValue = false,
  isBatchMode = false,
  selectedCompanies = [],
  initialCreateMode = "template",
  enableKaerInternalTab = false,
  prefilledResponsibleSubject,
  forcedTextTarget,
  showTextTargetWhenHidden = false,
  selectedTableRows = [],
}) => {
  const { t } = useTranslation();
  const { handleError, errorModal } = useErrorModal();
  const [options, setOptions] = useState<IDropDownOption[]>([]);
  const [buildingOptions, setBuildingOptions] = useState<IDropDownOption[]>([]);
  const [folderOptions, setFolderOptions] = useState<IDropDownOption[]>([]);
  const [templateOptions, setTemplateOptions] = useState<IDropDownOption[]>([]);
  const [gbuTemplateId, setGbuTemplateId] = useState<string>("");
  const [resolvedTodoToEdit, setResolvedTodoToEdit] = useState<any>(null);
  const resolvedInitialCreateMode: TodoModalModes =
    enableKaerInternalTab && initialCreateMode === "kaer_internal"
      ? "kaer_internal"
      : initialCreateMode === "manual"
      ? "manual"
      : "template";
  const [createMode, setCreateMode] = useState<TodoModalModes>(
    resolvedInitialCreateMode
  );
  const [templateVariables, setTemplateVariables] = useState<
    Array<{ id: string; key: string; value: string }>
  >([{ id: "1", key: "", value: "" }]);
  const [templatePreviewTitle, setTemplatePreviewTitle] = useState<string>("");
  const [templatePreviewDescription, setTemplatePreviewDescription] =
    useState<string>("");
  const todoRef = useRef<any>(null);
  const buildingDropdownRef = useRef<any>(null);
  const lastInitializedTodoIdRef = useRef<string | null>(null);
  const previousTypeRef = useRef<string | undefined>(undefined);

  const persistedSubjectSearchValue = useSelector(
    (state: RootState) => state.subjectSearch
  );
  const getInitialSubjectValue = () => {
    if (resolvedInitialCreateMode === "kaer_internal") {
      return prefilledResponsibleSubject?.value ?? "";
    }
    if (preExistingSubject?.value) {
      return preExistingSubject.value;
    }
    if (
      usePersistedSubjectSearchValue &&
      persistedSubjectSearchValue &&
      Object.keys(persistedSubjectSearchValue).length !== 0
    ) {
      return persistedSubjectSearchValue.value;
    }
    return "";
  };
  const getInitialTypeValue = () => {
    if (isBatchMode) return "";
    if (mode === "edit" && Boolean(todoToEdit?.id)) return "";
    if (resolvedInitialCreateMode === "manual") return "";
    return "text";
  };
  const [selectedSubject, setSelectedSubject] = useState(
    getInitialSubjectValue()
  );

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  const isEditMode = mode === "edit" && Boolean(todoToEdit?.id);
  const doneOptions = [
    { value: true, label: t("yes") },
    { value: false, label: t("no") },
  ];

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    setError: setFormError,
    clearErrors,
    trigger,
    watch,
  } = useForm({
    shouldFocusError: false,
    defaultValues: {
      subject: isBatchMode ? "" : getInitialSubjectValue(),
      name: prefilledName,
      type: getInitialTypeValue(),
      building: "",
      description: "",
      target: {},
      target_folder: "",
      target_filename: "",
      target_link: "",
      target_linkTitle: "",
      done: false,
      due_date: defaultCreateDueDate(
        prefilledDueDate,
        resolvedInitialCreateMode
      ),
      template: "",
      template_variables: {},
      is_exclusion_list: false,
      include_children: false,
      parents_only: false,
    },
    mode: "onTouched",
  });

  const watchedType = watch("type");
  const watchedTemplate = watch("template");
  const watchedName = watch("name");
  const watchedDescription = watch("description");
  const watchedSubject = watch("subject");
  const watchedBuilding = watch("building");
  const watchedTargetFolder = watch("target_folder");
  const watchedTargetLink = watch("target_link");
  const watchedTargetLinkTitle = watch("target_linkTitle");
  const parentsOnlySelected = watch("parents_only");
  const includeChildrenSelected = watch("include_children");
  const isExclusionList = watch("is_exclusion_list");
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField({
      fieldOrder: [
        "subject",
        "template",
        "template_variables",
        "name",
        "description",
        "type",
        "target_folder",
        "target_filename",
        "target_link",
        "target_linkTitle",
        "target",
        "due_date",
      ],
    });

  const showAfterSubjectSelected = isBatchMode || Boolean(watchedSubject);

  const editSubjectOption =
    isEditMode && (resolvedTodoToEdit ?? todoToEdit)?.subject?.id
      ? {
          value: (resolvedTodoToEdit ?? todoToEdit).subject.id,
          label: (resolvedTodoToEdit ?? todoToEdit).subject.name,
        }
      : undefined;

  const dropdownKeyBase = `${open ? "open" : "closed"}-${
    isEditMode ? todoToEdit?.id : "create"
  }`;

  const selectedTemplateOption = templateOptions.find(
    (o: any) => o?.value === watchedTemplate
  );
  const selectedBuildingOption = buildingOptions.find(
    (o: any) => o?.value === watchedBuilding
  );
  const selectedFolderOption = folderOptions.find(
    (o: any) => o?.value === watchedTargetFolder
  );

  const [manualDraft, setManualDraft] = useState<{
    name: string;
    description: string;
  }>({
    name: prefilledName || "",
    description: "",
  });

  const isTemplateMode = createMode === "template";
  const isKaerInternalMode = createMode === "kaer_internal";
  const isManualLikeMode = createMode !== "template";
  const hasSelectedTemplate = isTemplateMode && Boolean(watchedTemplate);
  const showTitleAndNameField =
    (isTemplateMode && showAfterSubjectSelected && hasSelectedTemplate) ||
    (!isTemplateMode && showAfterSubjectSelected);
  const showTypeField =
    !isKaerInternalMode &&
    showAfterSubjectSelected &&
    (!isTemplateMode || Boolean(watchedTemplate));
  const currentTodoType = (resolvedTodoToEdit ?? todoToEdit)?.type;
  const isGbuTemplateById = Boolean(
    gbuTemplateId && watchedTemplate === gbuTemplateId
  );
  const isGbuTemplateByLabel =
    String(selectedTemplateOption?.label || "") === GBU_TEMPLATE_NAME;
  const isGbuTemplateByEditedTodo =
    Boolean(watchedTemplate) &&
    (resolvedTodoToEdit ?? todoToEdit)?.template?.id === watchedTemplate &&
    (resolvedTodoToEdit ?? todoToEdit)?.template?.name === GBU_TEMPLATE_NAME;
  const isGbuTemplateSelected =
    isGbuTemplateById || isGbuTemplateByLabel || isGbuTemplateByEditedTodo;
  const isTypeLockedToGbu = isTemplateMode && isGbuTemplateSelected;
  const shouldIncludeGbuTypeOption =
    isTemplateMode &&
    (isGbuTemplateSelected ||
      watchedType === "gbu" ||
      (isEditMode && currentTodoType === "gbu"));
  const isTypeDropdownDisabled = isTypeLockedToGbu;
  const shouldShowTextTargetSection =
    watchedType === "text" &&
    (!hideNonMandatoryFields || showTextTargetWhenHidden) &&
    (!isTemplateMode || Boolean(watchedTemplate));
  const hasRenderableTargetFields =
    watchedType === "upload" ||
    shouldShowTextTargetSection ||
    watchedType === "gbu";
  const firstInvalidTemplateVariableId = templateVariables.find(
    (variable) => !variable.value || variable.value.trim() === "",
  )?.id;

  const typeOptions = (() => {
    const base = [
      { value: "upload", label: t("file_upload") },
      { value: "text", label: t("string") },
    ];
    if (isEditMode && currentTodoType === "form") {
      base.push({ value: "form", label: t("form") });
    }
    if (shouldIncludeGbuTypeOption) {
      base.push({ value: "gbu", label: t("gbu") });
    }
    return base;
  })();
  const selectedTypeOption =
    typeOptions.find((o: any) => o?.value === watchedType) ?? null;

  const extractVariablesFromText = (text: string): string[] => {
    if (!text || typeof text !== "string") return [];
    const regex = /\{\{\s*([^}|]+)(?:\|.*?)?\s*\}\}/g;
    const variables: string[] = [];
    let match;

    while ((match = regex.exec(text)) !== null) {
      const variableName = match[1].trim();
      if (variableName && !variables.includes(variableName)) {
        variables.push(variableName);
      }
    }

    return variables;
  };

  useEffect(() => {
    if (open) {
      api.genericApiRequest({
        entity: "todoTemplate",
        method: "get",
        parametersToRender: {
          depth: "0",
        },
        getAllPages: true,
        successHandler: (res: any) => {
          const templates = res.data?.results || [];
          const options = templates.map((template: any) => ({
            value: template.id,
            label: template.name,
          }));
          const gbuTemplate = templates.find(
            (template: any) => template?.name === GBU_TEMPLATE_NAME
          );
          setGbuTemplateId(gbuTemplate?.id || "");
          setTemplateOptions(options);
        },
        failHandler: (error: any) => {
          handleError(error);
          setGbuTemplateId("");
          setTemplateOptions([]);
        },
      });
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    if (!isEditMode) return;
    if (!todoToEdit?.id) return;

    setResolvedTodoToEdit(null);
    api.genericApiRequest({
      entity: "todo",
      method: "get",
      entityId: todoToEdit.id,
      parametersToRender: { depth: "1" },
      successHandler: (res: any) => {
        setResolvedTodoToEdit(res.data);
      },
      failHandler: () => {
        setResolvedTodoToEdit(todoToEdit);
      },
    });
  }, [open, isEditMode, todoToEdit?.id]);

  useEffect(() => {
    if (!open || watchedType !== "upload") {
      setFolderOptions([]);
      return;
    }
    const canFetch = isBatchMode || !!selectedSubject;

    if (!canFetch) {
      setFolderOptions([]);
      return;
    }

    const additionalUrlParameters: Record<string, string> = {};

    if (isBatchMode) {
      additionalUrlParameters["tree__name"] = "default_manage";
    }

    if (selectedSubject) {
      additionalUrlParameters["impersonate_subject"] = selectedSubject;
    }

    api.genericApiRequest({
      entity: "folder",
      method: "get",
      parametersToRender: {
        depth: "0",
        additionalUrlParameters,
      },
      successHandler: (res: any) => {
        const sorted = (res.data?.results || []).sort((a: any, b: any) =>
          String(a?.name || "").localeCompare(String(b?.name || ""))
        );
        setFolderOptions(
          sorted.map((item: any) => ({
            value: item.id,
            label: item.name,
          }))
        );
      },
      failHandler: (error: any) => {
        handleError(error);
        setFolderOptions([]);
      },
    });
  }, [open, watchedType, selectedSubject, isBatchMode]);

  useEffect(() => {
    if (watchedTemplate && open) {
      api.genericApiRequest({
        entity: "todoTemplate",
        method: "get",
        entityId: watchedTemplate,
        parametersToRender: {
          depth: "0",
        },
        successHandler: (res: any) => {
          const template = res.data;
          if (template) {
            const rawTitle = template.title || "";
            let rawDescription = "";
            if (typeof template.description === "string") {
              rawDescription = template.description;
            } else if (
              template.description &&
              typeof template.description === "object"
            ) {
              rawDescription = JSON.stringify(template.description, null, 2);
            }
            setTemplatePreviewTitle(rawTitle);
            setTemplatePreviewDescription(rawDescription);

            const titleVars = extractVariablesFromText(template.title || "");
            const descVars = template.description
              ? typeof template.description === "string"
                ? extractVariablesFromText(template.description)
                : typeof template.description === "object"
                ? Object.values(template.description).flatMap((val: any) =>
                    extractVariablesFromText(String(val))
                  )
                : []
              : [];

            const allVariables = Array.from(
              new Set([...titleVars, ...descVars])
            );

            if (allVariables.length > 0) {
              const existing = isEditMode
                ? todoToEdit?.template_variables || {}
                : {};
              const vars = allVariables.map((varName, index) => ({
                id: `var-${index}`,
                key: varName,
                value: String((existing as any)[varName] ?? ""),
              }));
              setTemplateVariables(vars);
            } else {
              setTemplateVariables([]);
              setValue("template_variables", {});
            }
          }
        },
        failHandler: (error: any) => {
          handleError(error);
        },
      });
    } else if (!watchedTemplate && open) {
      setTemplateVariables([]);
      setValue("template_variables", {});
      setTemplatePreviewTitle("");
      setTemplatePreviewDescription("");
      if (isTemplateMode) {
        if (!isEditMode) {
          setValue("name", "");
          setValue("description", "");
        }
      }
    }
  }, [watchedTemplate, open, setValue, isTemplateMode, isEditMode]);

  useEffect(() => {
    if (isEditMode) return;
    if (createMode === "kaer_internal") {
      setOptions(
        prefilledResponsibleSubject?.value ? [prefilledResponsibleSubject] : []
      );
      return;
    }
    const mergedOptions = [
      preExistingSubject,
      usePersistedSubjectSearchValue &&
      persistedSubjectSearchValue &&
      Object.keys(persistedSubjectSearchValue).length !== 0
        ? persistedSubjectSearchValue
        : undefined,
    ].filter(Boolean) as IDropDownOption[];
    const uniqueOptions = mergedOptions.filter(
      (option, index, array) =>
        array.findIndex((current) => current.value === option.value) === index
    );
    setOptions(uniqueOptions);
  }, [
    isEditMode,
    createMode,
    preExistingSubject,
    prefilledResponsibleSubject,
    usePersistedSubjectSearchValue,
    persistedSubjectSearchValue,
  ]);

  useEffect(() => {
    if (!open) return;
    if (isEditMode) return;
    if (createMode === "kaer_internal") {
      if (prefilledResponsibleSubject?.value) {
        setSelectedSubject(prefilledResponsibleSubject.value);
        setValue("subject", prefilledResponsibleSubject.value);
      } else {
        setSelectedSubject("");
        setValue("subject", "");
      }
      return;
    }
    const initialSubjectOption = preExistingSubject?.value
      ? preExistingSubject
      : usePersistedSubjectSearchValue &&
        persistedSubjectSearchValue &&
        Object.keys(persistedSubjectSearchValue).length !== 0
      ? persistedSubjectSearchValue
      : undefined;
    if (initialSubjectOption?.value) {
      setSelectedSubject(initialSubjectOption.value);
      setValue("subject", initialSubjectOption.value);
    }
  }, [
    open,
    isEditMode,
    createMode,
    prefilledResponsibleSubject,
    preExistingSubject,
    usePersistedSubjectSearchValue,
    persistedSubjectSearchValue,
    setValue,
  ]);

  useEffect(() => {
    if (!open) return;
    if (isEditMode) return;
    setValue("name", prefilledName);
    setValue(
      "due_date",
      defaultCreateDueDate(prefilledDueDate, resolvedInitialCreateMode)
    );
    if (resolvedInitialCreateMode === "manual" || isBatchMode) {
      setValue("type", "");
    } else {
      setValue("type", "text");
    }
    if (resolvedInitialCreateMode === "kaer_internal" && forcedTextTarget) {
      setValue("target_link", forcedTextTarget.link || "");
      setValue("target_linkTitle", forcedTextTarget.linkTitle || "");
    }
  }, [
    open,
    isEditMode,
    prefilledName,
    prefilledDueDate,
    isBatchMode,
    resolvedInitialCreateMode,
    forcedTextTarget,
    setValue,
  ]);

  useEffect(() => {
    if (!open) return;
    if (isEditMode) return;
    setCreateMode(resolvedInitialCreateMode);
    setManualDraft({
      name: prefilledName || "",
      description: "",
    });
  }, [open, isEditMode, prefilledName, resolvedInitialCreateMode]);

  useEffect(() => {
    if (!open) return;
    if (!isEditMode) return;
    if (resolvedTodoToEdit === null) return;
    const todo = resolvedTodoToEdit;
    if (!todo?.id) return;
    if (lastInitializedTodoIdRef.current === todo.id) return;
    lastInitializedTodoIdRef.current = todo.id;

    const hasTemplate = Boolean(todo?.template?.id);
    setCreateMode(hasTemplate ? "template" : "manual");

    const subjectId = todo?.subject?.id || "";
    const subjectName = todo?.subject?.name || "";
    if (subjectId) {
      setOptions([{ value: subjectId, label: subjectName }]);
      setSelectedSubject(subjectId);
      setValue("subject", subjectId);
    }

    setValue("type", todo?.type || "");
    setValue("done", Boolean(todo?.done));
    setValue("due_date", todo?.due_date ? dayjs(todo.due_date) : dayjs());

    const existingBuildingId =
      typeof todo?.building === "string"
        ? todo.building
        : todo?.building?.id || "";
    setValue("building", existingBuildingId);

    setValue("target", todo?.target || {});
    setValue("target_folder", todo?.target?.folder || "");
    setValue("target_filename", todo?.target?.filename || "");
    setValue("target_link", todo?.target?.link || "");
    setValue("target_linkTitle", todo?.target?.linkTitle || "");

    if (hasTemplate) {
      setValue("template", todo.template.id);
      setValue("template_variables", todo?.template_variables || {});
    } else {
      setValue("template", "");
      setValue("template_variables", {});
    }

    const todoTitle = todo?.title || "";
    const todoDesc = todo?.description;
    setValue("name", todoTitle);
    if (typeof todoDesc === "string") {
      setValue("description", todoDesc);
    } else if (todoDesc && typeof todoDesc === "object") {
      setValue("description", JSON.stringify(todoDesc, null, 2));
    } else {
      setValue("description", "");
    }

    setManualDraft({
      name: todoTitle,
      description: typeof todoDesc === "string" ? todoDesc : "",
    });

    setTemplateVariables([]);
  }, [open, isEditMode, resolvedTodoToEdit, setValue]);

  useEffect(() => {
    if (!open) return;
    if (previousTypeRef.current === undefined) {
      previousTypeRef.current = watchedType;
      return;
    }
    if (previousTypeRef.current === watchedType) return;

    if (
      createMode === "kaer_internal" &&
      watchedType === "text" &&
      forcedTextTarget
    ) {
      setValue("target_link", forcedTextTarget.link || "");
      setValue("target_linkTitle", forcedTextTarget.linkTitle || "");
      previousTypeRef.current = watchedType;
      return;
    }

    setValue("target_folder", "");
    setValue("target_filename", "");
    setValue("target_link", "");
    setValue("target_linkTitle", "");
    setValue("target", {});
    clearErrors([
      "target_folder",
      "target_filename",
      "target_link",
      "target_linkTitle",
      "target",
    ]);

    previousTypeRef.current = watchedType;
  }, [open, watchedType, setValue, clearErrors, createMode, forcedTextTarget]);

  useEffect(() => {
    if (!open) return;
    if (watchedType !== "text") return;
    trigger(["target_link", "target_linkTitle"]);
  }, [open, watchedType, watchedTargetLink, watchedTargetLinkTitle, trigger]);

  useEffect(() => {
    if (!open) return;
    if (createMode !== "manual") return;

    setManualDraft({
      name: typeof watchedName === "string" ? watchedName : "",
      description:
        typeof watchedDescription === "string" ? watchedDescription : "",
    });
  }, [open, createMode, watchedName, watchedDescription]);

  useEffect(() => {
    if (!open) return;
    if (!isTemplateMode) return;

    if (isTypeLockedToGbu) {
      if (watchedType === "gbu") return;
      setValue("type", "gbu", {
        shouldValidate: true,
        shouldDirty: true,
      });
      clearErrors("type");
      return;
    }

    if (watchedType === "gbu" && watchedTemplate) {
      setValue("type", "text", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [
    open,
    isTemplateMode,
    isTypeLockedToGbu,
    watchedType,
    watchedTemplate,
    setValue,
    clearErrors,
  ]);

  useEffect(() => {
    if (!selectedSubject) {
      setBuildingOptions([]);
      return;
    }

    const fetchBuildings = (subjectId: string) => {
      const additionalUserParameters: any = {};
      additionalUserParameters["subject"] = subjectId;
      api.genericApiRequest({
        entity: "building",
        method: "get",
        parametersToRender: {
          additionalUrlParameters: additionalUserParameters,
          depth: "0",
        },
        successHandler: (res: any) => {
          const buildingOptions = res.data.results.map((item: any) => ({
            value: item.id,
            label:
              item.name + ` (${item.street_house}, ${item.zip} ${item?.city} )`,
          }));
          setBuildingOptions(buildingOptions);
        },
        failHandler: (error: any) => {
          handleError(error);
          setBuildingOptions([]);
        },
      });
    };

    api.genericApiRequest({
      entity: "subject",
      method: "get",
      entityId: selectedSubject,
      parametersToRender: { depth: "0" },
      successHandler: (res: any) => {
        const subjectData = res.data;
        if (subjectData.is_company) {
          fetchBuildings(subjectData.id);
        } else if (subjectData.company && subjectData.company.subject_id) {
          fetchBuildings(subjectData.company.subject_id);
        } else {
          setBuildingOptions([]);
        }
      },
      failHandler: (error: any) => {
        handleError(error);
        setBuildingOptions([]);
      },
    });
  }, [selectedSubject]);

  useEffect(() => {
    if (!open) {
      reset();
      handleError(undefined);
      setTemplateVariables([{ id: "1", key: "", value: "" }]);
      lastInitializedTodoIdRef.current = null;
      setResolvedTodoToEdit(null);
      setFolderOptions([]);
      previousTypeRef.current = undefined;
      setGbuTemplateId("");
    }
  }, [open, reset]);

  const resetAsyncInput = () => {
    setSelectedSubject("");
  };

  const handleTemplateVariableChange = (
    id: string,
    field: "key" | "value",
    newValue: string
  ) => {
    const updated = templateVariables.map((v) =>
      v.id === id ? { ...v, [field]: newValue } : v
    );
    setTemplateVariables(updated);

    const obj: any = {};
    updated.forEach((v) => {
      if (v.key && v.value) {
        obj[v.key] = v.value;
      }
    });
    setValue("template_variables", Object.keys(obj).length > 0 ? obj : {}, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSubmit = async (data: any) => {
    try {
      /* eslint-disable */
      const {
        template,
        template_variables,
        target_folder,
        target_filename,
        target_link,
        target_linkTitle,
        ...rest
      } = data;
      /* eslint-enable */

      const submitData: any = {
        ...rest,
        due_date: data.due_date ? dayjs(data.due_date).format() : null,
      };

      if (data.type === "upload") {
        submitData.target = {
          folder: target_folder,
          filename: target_filename,
        };
      } else if (createMode === "kaer_internal" && forcedTextTarget) {
        submitData.target = {
          link: forcedTextTarget.link,
          linkTitle: forcedTextTarget.linkTitle,
        };
        submitData.type = "text";
      } else if (data.type === "text") {
        const link = String(target_link || "").trim();
        const title = String(target_linkTitle || "").trim();
        const hasAny = Boolean(link) || Boolean(title);
        submitData.target = hasAny
          ? {
              link: target_link,
              linkTitle: target_linkTitle,
            }
          : {};
      }

      if (template && createMode === "template") {
        submitData.template = template;

        if (templateVariables.length === 0) {
          submitData.template_variables = {};
        } else {
          const obj: any = {};
          templateVariables.forEach((v) => {
            if (v.key) {
              obj[v.key] = v.value ?? "";
            }
          });
          submitData.template_variables = obj;
        }

        delete submitData.name;
        delete submitData.description;
      } else {
        submitData.title = data.name;
        submitData.description = data.description;

        if (isEditMode) {
          submitData.template = null;
          submitData.template_variables = {};
        }

        delete submitData.name;
      }

      if (isBatchMode) {
        const tempCompanySubjectSet =
          selectedTableRows.length > 0
            ? selectedTableRows
                .map((row: any) => row.subject?.id || row.subject_id)
                .filter(Boolean)
            : [];
        submitData.company_subject_set = tempCompanySubjectSet;
        delete submitData.subject;
        delete submitData.building;
      }

      delete submitData.target_folder;
      delete submitData.target_filename;
      delete submitData.target_link;
      delete submitData.target_linkTitle;

      await api.genericApiRequest({
        entity: "todo",
        method: isEditMode ? "PUT" : "POST",
        ...(isEditMode ? { entityId: todoToEdit.id } : {}),
        submitData,
        successHandler: () => {
          onClose();
          reset();
          onSuccess();
        },
        failHandler: (error: any) => {
          handleError(error);
        },
      });
    } catch (error) {
      handleError(error);
    }
  };

  const getTranslationKey = () => {
    const count = selectedTableRows.length;

    if (count === 0) {
      if (parentsOnlySelected)
        return "no_companies_selected_create_batchtodo_for_all_parents_only";
      return "no_companies_selected_create_batchtodo_for_all";
    }

    if (isExclusionList) {
      if (parentsOnlySelected) {
        return "create_batchtodo_for_all_companies_except_selected_parents_only";
      }
      if (includeChildrenSelected) {
        return "create_batchtodo_for_all_companies_except_selected_include_children";
      }
      return "create_batchtodo_for_all_companies_except_selected_companies";
    }

    if (parentsOnlySelected && !isExclusionList) {
      return "create_batchtodo_for_selected_companies_parents_only";
    }

    if (includeChildrenSelected) {
      return "create_batchtodo_for_all_selected_companies_include_children";
    }

    return "create_batchtodo_for_selected_companies";
  };

  const handleEscapeClose = () => {
    onClose();
    reset();
  };
  useModalDismiss(open, handleEscapeClose);

  return (
    <Modal
      open={open}
      disableAutoFocus
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
        reset();
      }}
    >
      <Box className={styles.createTodoModal}>
        <Typography variant="h6" component="h2" gutterBottom align="center">
          {isEditMode
            ? t("edit_todo")
            : isBatchMode
            ? t("create_todo_batch")
            : t("create_todo")}
        </Typography>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit, (errors) => {
            focusFirstInvalidField(errors);
          })}
        >
          <Stack spacing={2}>
            <Box>
              <Tabs
                value={createMode}
                onChange={(_event, newValue: TodoModalModes) => {
                  setCreateMode(newValue);
                  if (!isEditMode && !isBatchMode) {
                    if (newValue === "manual") {
                      setValue("type", "");
                    } else {
                      setValue("type", "text");
                    }
                  }

                  if (newValue === "manual") {
                    setValue("template", "");
                    setValue("template_variables", {});
                    setTemplateVariables([]);
                    setValue("name", manualDraft.name || "");
                    setValue("description", manualDraft.description || "");
                    if (!isEditMode && !isBatchMode) {
                      setValue("due_date", dueDateInDaysAtNoon(14));
                    }
                    if (preExistingSubject?.value) {
                      setSelectedSubject(preExistingSubject.value);
                      setValue("subject", preExistingSubject.value);
                    }
                  } else if (newValue === "kaer_internal") {
                    setValue("template", "");
                    setValue("template_variables", {});
                    setTemplateVariables([]);
                    setValue("name", prefilledName || "");
                    setValue("description", "");
                    if (!isEditMode) {
                      setValue(
                        "due_date",
                        prefilledDueDate ?? dueDateInDaysAtNoon(7)
                      );
                    }
                    if (prefilledResponsibleSubject?.value) {
                      setSelectedSubject(prefilledResponsibleSubject.value);
                      setValue("subject", prefilledResponsibleSubject.value);
                    } else {
                      setSelectedSubject("");
                      setValue("subject", "");
                    }
                    if (forcedTextTarget) {
                      setValue("target_link", forcedTextTarget.link || "");
                      setValue(
                        "target_linkTitle",
                        forcedTextTarget.linkTitle || ""
                      );
                    }
                  } else {
                    if (isEditMode && todoToEdit) {
                      const todoTitle = todoToEdit?.title || "";
                      const todoDesc = todoToEdit?.description;
                      setValue("name", todoTitle);
                      if (typeof todoDesc === "string") {
                        setValue("description", todoDesc);
                      } else if (todoDesc && typeof todoDesc === "object") {
                        setValue(
                          "description",
                          JSON.stringify(todoDesc, null, 2)
                        );
                      } else {
                        setValue("description", "");
                      }
                      if (todoToEdit?.template?.id) {
                        setValue("template", todoToEdit.template.id);
                      }
                    } else {
                      setValue("name", "");
                      setValue("description", "");
                      if (!isEditMode && !isBatchMode) {
                        setValue("due_date", dueDateInDaysAtNoon(14));
                      }
                      if (preExistingSubject?.value) {
                        setSelectedSubject(preExistingSubject.value);
                        setValue("subject", preExistingSubject.value);
                      }
                    }
                  }
                }}
                variant="fullWidth"
              >
                {enableKaerInternalTab && (
                  <Tab value="kaer_internal" label={t("kaer_internal")} />
                )}
                <Tab value="template" label={t("use_template")} />
                <Tab value="manual" label={t("manual")} />
              </Tabs>
            </Box>
            {!isBatchMode && (
              <Grid item ref={registerValidationTarget("subject")}>
                <Controller
                  name="subject"
                  control={control}
                  rules={{
                    required: !isBatchMode ? t("field_required") : false,
                  }}
                  render={({ field }) => (
                    <>
                      <FormFieldLabel
                        label={
                          isKaerInternalMode
                            ? t("responsible_subject_person")
                            : t("person_or_company")
                        }
                        htmlFor="todo-subject"
                        mandatory
                      />
                      <DropDownSubjectSearch
                        key={`subject-${dropdownKeyBase}`}
                        name="todo-subject"
                        options={options}
                        preExistingValue={
                          isKaerInternalMode
                            ? editSubjectOption || prefilledResponsibleSubject
                            : editSubjectOption ||
                              preExistingSubject ||
                              (usePersistedSubjectSearchValue &&
                              persistedSubjectSearchValue &&
                              Object.keys(persistedSubjectSearchValue)
                                .length !== 0
                                ? persistedSubjectSearchValue
                                : undefined)
                        }
                        onChange={(selectedOption: any) => {
                          if (selectedOption && selectedOption.value) {
                            field.onChange(selectedOption.value);
                            setSelectedSubject(selectedOption.value);
                          } else {
                            field.onChange("");
                            setSelectedSubject("");
                            setBuildingOptions([]);
                            setValue("building", "");
                            if (buildingDropdownRef.current) {
                              buildingDropdownRef.current.clearValue();
                            }
                          }
                        }}
                        subdomain={subdomain}
                        queryParameter={
                          isKaerInternalMode ? "users" : undefined
                        }
                        companyIdForFiltering={
                          isKaerInternalMode ? KAER_COMPANY_ID : undefined
                        }
                        resetInput={resetAsyncInput}
                        customRef={todoRef}
                        autofocus={true}
                        includeDeletedSubjects={false}
                        error={!!errors.subject}
                      />
                      {errors.subject && (
                        <Typography color="error" variant="caption">
                          {String(errors.subject.message)}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Grid>
            )}

            {isTemplateMode && showAfterSubjectSelected && (
              <Grid item ref={registerValidationTarget("template")}>
                <Controller
                  name="template"
                  control={control}
                  rules={{
                    required: isTemplateMode ? t("field_required") : false,
                  }}
                  render={({ field }) => (
                    <>
                      <DropDown
                        key={`template-${dropdownKeyBase}-${templateOptions.length}`}
                        options={templateOptions}
                        useReduxDefaultValue={true}
                        defaultValue={selectedTemplateOption ?? null}
                        onChange={(selectedOption: any) => {
                          if (selectedOption && selectedOption.value) {
                            field.onChange(selectedOption.value);
                            const selectedIsGbuTemplate =
                              (gbuTemplateId &&
                                selectedOption.value === gbuTemplateId) ||
                              String(selectedOption.label || "") ===
                                GBU_TEMPLATE_NAME;
                            if (selectedIsGbuTemplate) {
                              setValue("type", "gbu", {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                              clearErrors("type");
                            } else {
                              setValue("type", "text", {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                              clearErrors("type");
                            }
                          } else {
                            field.onChange("");
                            setValue("type", "", {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                        }}
                        label={t("todo_template")}
                        mandatory={true}
                        required={true}
                      />
                      {errors.template && (
                        <Typography color="error" variant="caption">
                          {String(errors.template.message)}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Grid>
            )}

            {hasSelectedTemplate && showAfterSubjectSelected && (
              <Grid item>
                <Typography>
                  {t("template_variables")}
                  <span className={styles.requiredRed}>*</span>
                </Typography>
                <Controller
                  name="template_variables"
                  control={control}
                  rules={{
                    validate: () =>
                      !isTemplateMode ||
                      !watchedTemplate ||
                      templateVariables.every(
                        (variable) =>
                          Boolean(variable.value) &&
                          variable.value.trim() !== "",
                      ) ||
                      t("field_required"),
                  }}
                  render={() => (
                    <>
                      {templateVariables.length > 0 ? (
                        <Stack
                          spacing={2}
                          className={styles.templateVariablesStack}
                        >
                          {templateVariables.map((variable) => (
                            <Stack
                              key={variable.id}
                              direction="row"
                              spacing={2}
                              className={styles.templateVariableRow}
                              ref={
                                variable.id === firstInvalidTemplateVariableId
                                  ? registerValidationTarget(
                                      "template_variables",
                                    )
                                  : undefined
                              }
                            >
                              <TextAreaWithRef
                                rows={1}
                                value={variable.key}
                                disabled
                                hideLabel
                                style={{ flex: 1 }}
                                fieldStyle={{ resize: "vertical" }}
                              />
                              <TextAreaWithRef
                                rows={1}
                                placeholder={t("value")}
                                value={variable.value}
                                onChange={(e) =>
                                  handleTemplateVariableChange(
                                    variable.id,
                                    "value",
                                    e.target.value,
                                  )
                                }
                                required
                                hideLabel
                                style={{ flex: 1 }}
                                fieldStyle={{ resize: "vertical" }}
                              />
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {t("no_variables_needed_for_template")}
                        </Typography>
                      )}
                      {errors.template_variables && (
                        <Typography color="error" variant="caption">
                          {String(errors.template_variables.message)}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Grid>
            )}

            {showTitleAndNameField && (
              <Grid
                item
                ref={
                  !isTemplateMode
                    ? registerValidationTarget("name")
                    : undefined
                }
                className={
                  isTemplateMode
                    ? styles.templateTitleFieldContainer
                    : undefined
                }
              >
                {isTemplateMode ? (
                  <TextAreaWithRef
                    label={`${t("title")} (${t("todo_template")})`}
                    labelClassname={styles.templateTitleFieldLabel}
                    value={templatePreviewTitle}
                    rows={1}
                    disabled
                    wrapperClassname={
                      styles.disabledTextFieldWithCursorNotAllowed
                    }
                    fieldStyle={{ resize: "vertical" }}
                  />
                ) : (
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      required: isManualLikeMode ? t("field_required") : false,
                    }}
                    render={({ field }) => (
                      <>
                        <TextAreaWithRef
                          {...field}
                          label={t("title")}
                          rows={1}
                          required
                          fieldStyle={{ resize: "vertical" }}
                        />
                        {errors.name && (
                          <Typography color="error" variant="caption">
                            {errors.name.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                )}
              </Grid>
            )}

            {isTemplateMode &&
              showAfterSubjectSelected &&
              hasSelectedTemplate && (
                <Grid item className={styles.templateDescriptionFieldContainer}>
                  <TextAreaWithRef
                    label={`${t("description")} (${t("todo_template")})`}
                    labelClassname={styles.templateDescriptionFieldLabel}
                    value={templatePreviewDescription}
                    rows={2}
                    disabled
                    wrapperClassname={
                      styles.disabledTextFieldWithCursorNotAllowed
                    }
                    fieldStyle={{ resize: "vertical" }}
                  />
                </Grid>
              )}

            {!isTemplateMode && showAfterSubjectSelected && (
              <Grid item ref={registerValidationTarget("description")}>
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    required: isManualLikeMode ? t("field_required") : false,
                  }}
                  render={({ field }) => (
                    <>
                      <TextAreaWithRef
                        {...field}
                        label={t("description")}
                        rows={2}
                        required
                        fieldStyle={{ resize: "vertical" }}
                      />
                      {errors.description && (
                        <Typography color="error" variant="caption">
                          {errors.description.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Grid>
            )}

            {showTypeField && (
              <Grid item ref={registerValidationTarget("type")}>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: t("field_required") }}
                  render={({ field }) => (
                    <>
                      <DropDown
                        key={`type-${dropdownKeyBase}-${typeOptions.length}`}
                        options={typeOptions}
                        defaultValue={watchedType || undefined}
                        externalValue={selectedTypeOption}
                        onChange={(selectedOption: any) => {
                          if (selectedOption && selectedOption.value) {
                            const selectedType = selectedOption.value;
                            field.onChange(selectedType);
                            if (
                              isTemplateMode &&
                              selectedType === "gbu" &&
                              gbuTemplateId &&
                              watchedTemplate !== gbuTemplateId
                            ) {
                              setValue("template", gbuTemplateId, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }
                          } else {
                            field.onChange("");
                          }
                        }}
                        mandatory={true}
                        label={t("type")}
                        required={true}
                        disabled={isTypeDropdownDisabled}
                      />
                      {errors.type && (
                        <Typography color="error" variant="caption">
                          {errors.type.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </Grid>
            )}

            {!isKaerInternalMode &&
              showAfterSubjectSelected &&
              hasRenderableTargetFields && (
                <Grid item>
                  {watchedType === "upload" && (
                    <Stack spacing={2}>
                      <Controller
                        name="target_folder"
                        control={control}
                        rules={{ required: t("field_required") }}
                        render={({ field }) => (
                          <div ref={registerValidationTarget("target_folder")}>
                            <DropDown
                              key={`target-folder-${dropdownKeyBase}-${folderOptions.length}`}
                              options={folderOptions}
                              useReduxDefaultValue={true}
                              defaultValue={selectedFolderOption ?? null}
                              onChange={(selectedOption: any) => {
                                if (selectedOption && selectedOption.value) {
                                  field.onChange(selectedOption.value);
                                  return;
                                }
                                field.onChange("");
                                setValue("target_folder", "");
                              }}
                              isClearable={true}
                              label={t("target_directory")}
                              mandatory={true}
                              required={true}
                            />
                            {errors.target_folder && (
                              <Typography color="error" variant="caption">
                                {String(errors.target_folder.message)}
                              </Typography>
                            )}
                          </div>
                        )}
                      />

                      <Controller
                        name="target_filename"
                        control={control}
                        rules={{ required: t("field_required") }}
                        render={({ field }) => (
                          <Box
                            className={styles.targetTextFieldWrapper}
                            ref={registerValidationTarget("target_filename")}
                          >
                            <InputWithRef
                              {...field}
                              label={t("file_name")}
                              required
                            />
                            {errors.target_filename && (
                              <Typography color="error" variant="caption">
                                {String(errors.target_filename.message)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      />
                    </Stack>
                  )}
                  {shouldShowTextTargetSection && (
                    <Stack spacing={2}>
                      <Controller
                        name="target_link"
                        control={control}
                        rules={{
                          validate: (value) => {
                            const hasLink = Boolean(
                              value && String(value).trim()
                            );
                            const hasTitle = Boolean(
                              watchedTargetLinkTitle &&
                                String(watchedTargetLinkTitle).trim()
                            );
                            if (hasTitle && !hasLink)
                              return t("field_required");
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <Box
                            className={styles.targetTextFieldWrapper}
                            ref={registerValidationTarget("target_link")}
                          >
                            <InputWithRef
                              {...field}
                              label={t("link_destination_optional")}
                              placeholder="https://"
                            />
                            {errors.target_link && (
                              <Typography color="error" variant="caption">
                                {String(errors.target_link.message)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      />

                      <Controller
                        name="target_linkTitle"
                        control={control}
                        rules={{
                          validate: (value) => {
                            const hasTitle = Boolean(
                              value && String(value).trim()
                            );
                            const linkValue = watchedTargetLink;
                            const hasLink = Boolean(
                              linkValue && String(linkValue).trim()
                            );
                            if (hasLink && !hasTitle)
                              return t("field_required");
                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <Box
                            className={styles.targetTextFieldWrapper}
                            ref={registerValidationTarget("target_linkTitle")}
                          >
                            <InputWithRef
                              {...field}
                              label={t("link_title_optional")}
                            />
                            {errors.target_linkTitle && (
                              <Typography color="error" variant="caption">
                                {String(errors.target_linkTitle.message)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      />
                    </Stack>
                  )}
                  {watchedType === "gbu" && (
                    <Controller
                      name="target"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (typeof value === "string") {
                            try {
                              JSON.parse(value);
                              return true;
                            } catch {
                              return t("invalid_json_error");
                            }
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <div ref={registerValidationTarget("target")}>
                          <TextAreaWithRef
                            {...field}
                            label={t("target")}
                            rows={4}
                            value={
                              typeof field.value === "string"
                                ? field.value
                                : JSON.stringify(field.value, null, 2)
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value);
                              try {
                                if (value) {
                                  JSON.parse(value);
                                  setFormError("target", {
                                    type: "manual",
                                    message: "",
                                  });
                                }
                              } catch {
                                // Keep error if JSON is invalid
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              if (!value) {
                                field.onChange({});
                                return;
                              }
                              try {
                                const parsed = JSON.parse(value);
                                field.onChange(parsed);
                                setFormError("target", {
                                  type: "manual",
                                  message: "",
                                });
                              } catch {
                                setFormError("target", {
                                  type: "manual",
                                  message: t("invalid_json_error"),
                                });
                              }
                            }}
                            fieldStyle={{ resize: "vertical" }}
                          />
                          {errors.target && (
                            <Typography color="error" variant="caption">
                              {String(errors.target.message)}
                            </Typography>
                          )}
                        </div>
                      )}
                    />
                  )}
                </Grid>
              )}

            {!hideNonMandatoryFields &&
              !isBatchMode &&
              showAfterSubjectSelected && (
                <Grid item>
                  <Controller
                    name="building"
                    control={control}
                    render={({ field }) => (
                      <DropDown
                        key={`building-${dropdownKeyBase}-${buildingOptions.length}`}
                        options={buildingOptions}
                        useReduxDefaultValue={true}
                        defaultValue={selectedBuildingOption ?? null}
                        onChange={(selectedOption: any) => {
                          if (selectedOption && selectedOption.value) {
                            field.onChange(selectedOption.value);
                            return;
                          }
                          field.onChange("");
                          setValue("building", "");
                        }}
                        isClearable={true}
                        label={t("building_location")}
                        customRef={buildingDropdownRef}
                        resetInput={() => {
                          field.onChange("");
                          setValue("building", "");
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

            {isEditMode &&
              !hideNonMandatoryFields &&
              showAfterSubjectSelected && (
                <Grid item>
                  <Controller
                    name="done"
                    control={control}
                    render={({ field }) => (
                      <DropDown
                        options={doneOptions}
                        onChange={(selectedOption: any) => {
                          if (selectedOption && selectedOption.value) {
                            field.onChange(selectedOption.value);
                          }
                        }}
                        label={t("done")}
                        defaultValue={false}
                      />
                    )}
                  />
                </Grid>
              )}

            {showAfterSubjectSelected && (
              <Grid item ref={registerValidationTarget("due_date")}>
                <Typography>
                  {t("due_date")}
                  <span className={styles.requiredRed}>*</span>
                </Typography>
                <Controller
                  name="due_date"
                  control={control}
                  rules={{ required: t("field_required") }}
                  render={({ field }) => (
                    <>
                      <GenericDateTime
                        value={field.value}
                        onChange={(newValue) => field.onChange(newValue)}
                        textFieldProps={{ fullWidth: true }}
                      />
                      {errors.due_date && (
                        <Typography color="error" variant="caption">
                          {String(errors.due_date.message)}
                        </Typography>
                      )}
                    </>
                  )}
                />
                {isBatchMode && (
                  <>
                    <Box className={styles.checkboxWrapper}>
                      <Controller
                        name="is_exclusion_list"
                        control={control}
                        render={({ field }) => (
                          <>
                            <FormControlLabel
                              label={t("is_exclusion_list")}
                              control={
                                <Checkbox
                                  {...field}
                                  value={field.value}
                                  checked={field.value}
                                  disabled={selectedTableRows.length === 0}
                                  onChange={(event) =>
                                    field.onChange(event.target.checked)
                                  }
                                />
                              }
                              className={styles.modalCheckbox}
                            />
                          </>
                        )}
                      />
                      <Controller
                        name="include_children"
                        control={control}
                        render={({ field }) => (
                          <>
                            <FormControlLabel
                              label={t("include_child_companies")}
                              control={
                                <Checkbox
                                  {...field}
                                  value={field.value}
                                  checked={field.value}
                                  onChange={(event) =>
                                    field.onChange(event.target.checked)
                                  }
                                  disabled={
                                    parentsOnlySelected ||
                                    selectedTableRows.length === 0
                                  }
                                />
                              }
                              className={styles.modalCheckbox}
                            />
                          </>
                        )}
                      />
                      <Controller
                        name="parents_only"
                        control={control}
                        render={({ field }) => (
                          <>
                            <FormControlLabel
                              label={t("only_parent_companies")}
                              control={
                                <Checkbox
                                  {...field}
                                  value={field.value}
                                  checked={field.value}
                                  onChange={(event) =>
                                    field.onChange(event.target.checked)
                                  }
                                  disabled={includeChildrenSelected}
                                />
                              }
                              className={styles.modalCheckbox}
                            />
                          </>
                        )}
                      />
                    </Box>
                    <div className={styles.infoContainer}>
                      <InfoOutlinedIcon />
                      <p className={styles.infoText}>
                        {t(getTranslationKey(), {
                          count: selectedTableRows.length,
                        })}
                      </p>
                    </div>
                  </>
                )}
              </Grid>
            )}

            <Box className={styles.createTodoModalButtons}>
              <GenericButton variant="outlined" onClick={onClose}>
                {t("cancel")}
              </GenericButton>
              <GenericButton
                variant="contained"
                type="submit"
                disabled={isBatchMode && selectedCompanies.length === 0}
              >
                {isEditMode ? t("save") : t("create")}
              </GenericButton>
            </Box>
          </Stack>
        </form>
        {errorModal}
      </Box>
    </Modal>
  );
};

export default TodoCreateEditModal;

// TODO: Add translations for Todo Templates, just like in examinationList view
